import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ElectricBoltIcon from "@mui/icons-material/ElectricBolt";
import SpeedIcon from "@mui/icons-material/Speed";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import moment from "moment";
import { usePowerData } from "../hooks/usePowerData";
import { formatDecimal } from "../utils/common";
import ChartToggle from "../components/ChartToggle";
import RoomPowerChart from "../components/RoomPowerChart";
import StatCard from "../components/StatCard";
import { ChartView } from "../types/power";

const DashboardPage = () => {
  const { rows, error, source } = usePowerData();
  const [chartView, setChartView] = useState<ChartView>("bar");

  const roomNames = useMemo(
    () => Array.from(new Set(rows.map((row) => row.name).filter(Boolean))),
    [rows],
  );

  const averageVoltage = useMemo(() => {
    const values = rows
      .map((row) => row.voltage)
      .filter((value): value is number => typeof value === "number");

    if (!values.length) {
      return 0;
    }

    return values.reduce((total, value) => total + value, 0) / values.length;
  }, [rows]);

  const totalActivePower = useMemo(
    () => rows.reduce((total, row) => total + (row.power_active ?? 0), 0),
    [rows],
  );

  const latestReading = useMemo(() => {
    if (!rows.length) {
      return null;
    }

    return [...rows].sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    )[0];
  }, [rows]);

  return (
    <Stack spacing={4}>
      <Stack spacing={1.5}>
        <Typography variant="h3">Energy Intelligence Dashboard</Typography>
        <Typography color="text.secondary" maxWidth={640}>
          Monitor room-level power activity, compare loads, and spot trends in
          real time. Built for modern energy teams who need clarity fast.
        </Typography>
        <Chip
          label={
            source === "api" ? "Live data connected" : "Cached data in use"
          }
          color={source === "api" ? "primary" : "default"}
          sx={{ alignSelf: "flex-start" }}
        />
      </Stack>

      {error ? <Alert severity="info">{error}</Alert> : null}

      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <StatCard
            label="Rooms monitored"
            value={`${roomNames.length || 0}`}
            helper="Active sensors reporting"
            icon={<HomeWorkIcon />}
            accent="#2d6cdf"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            label="Avg voltage"
            value={`${formatDecimal(averageVoltage, 1) || 0} V`}
            helper="Across the last readings"
            icon={<SpeedIcon />}
            accent="#0b8e8b"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            label="Total active power"
            value={`${formatDecimal(totalActivePower, 1) || 0} W`}
            helper="Summed across rooms"
            icon={<ElectricBoltIcon />}
            accent="#f0a832"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            label="Latest reading"
            value={
              latestReading
                ? moment(latestReading.created_at).format("h:mm A")
                : "--"
            }
            helper={
              latestReading
                ? moment(latestReading.created_at).format("MMM D, YYYY")
                : "No recent data"
            }
            icon={<AccessTimeIcon />}
            accent="#6e58bd"
          />
        </Grid>
      </Grid>

      <Paper
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 4,
          border: "1px solid rgba(13, 27, 42, 0.06)",
          background: "rgba(255, 255, 255, 0.95)",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
        >
          <Box>
            <Typography variant="h5">Room Power Consumption</Typography>
            <Typography color="text.secondary">
              Compare average power per room or inspect trends across time.
            </Typography>
          </Box>
          <ChartToggle value={chartView} onChange={setChartView} />
        </Stack>
        <Box sx={{ mt: 3 }}>
          <RoomPowerChart rows={rows} view={chartView} metric="power_active" />
        </Box>
      </Paper>
    </Stack>
  );
};

export default DashboardPage;
